<template>
    <div class="metadata-rule">
        <template v-if="!isLocaleEnglish">
            <div class="flex-break" />
            <strong>{{ $t('Criteria') }}</strong>
            <div class="flex-break" />
        </template>
        <simple-select
            :options="operatorOptions"
            :field="operatorField"
            :check-valid-rule="checkValidRule"
            :value="operatorValue"
            :disabled="readOnly"
            preselect-first
            @input="$emit('input', $event)" />
        <template v-if="showValueComponent">
            <component
                :is="valueComponent.component"
                v-bind="valueComponent.props"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                :loading-resources="loadingResources"
                @input="processInput($event)" />
        </template>
    </div>
</template>

<script>
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/components/absolute-date-range.vue';
import CsvUpload from '@/segments/components/segment-editor-vue/components/csv-upload.vue';
import MetadataValueSelect from '@/segments/components/segment-editor-vue/components/metadata-value-select.vue';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import ResourceSelect from '@/segments/components/segment-editor-vue/components/resource-select.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import SingleDate from '@/segments/components/segment-editor-vue/components/single-date.vue';
import StringInput from '@/segments/components/segment-editor-vue/components/string-input.vue';
import isNumber from 'lodash/isNumber';

import { deviceValuesByField } from '@/constants/device-props';
import {
    getAbsoluteDateRangeConfig,
    getCsvConfig,
    getNumberConfig,
    getOperatorOptions,
    getRelativeDateRangeConfig,
    getSingleDateConfig,
    getStringConfig,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { MOBILE_VERSION_RULES_LIST } from '@/utils/segments.js';

export default {
    name: 'MetadataRule',
    components: {
        AbsoluteDateRange,
        CsvUpload,
        NumberInput,
        RelativeDateRange,
        ResourceSelect,
        SimpleSelect,
        SingleDate,
        StringInput,
        MetadataValueSelect,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        loadingResources: {
            type: Boolean,
            required: false,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    computed: {
        operatorField () {
            if (this.rule.schema === 'boolean' || this.rule.schema === 'expression') {
                return ['value', 'operator'];
            }

            return ['operator'];
        },
        operatorOptions () {
            if (this.rule.schema !== undefined || this.rule.id !== undefined) {
                return getOperatorOptions({ schema: this.rule.schema, id: this.rule.id });
            }

            return undefined;
        },
        operatorValue () {
            if (this.rule.schema === 'boolean' || this.rule.schema === 'expression') {
                return this.operatorOptions?.find((option) => option.value === this.rule.value);
            }

            return this.operatorOptions?.find((option) => option.operator === this.rule.operator);
        },
        showValueComponent () {
            return this.operatorValue && !this.operatorValue.unary;
        },
        valueComponent () {
            if (this.rule.id === 'visitor_sample_group') {
                const value = isNumber(this.rule.value) ? this.rule.value : 0;
                const component = getNumberConfig(this.rule, 'value');
                component.props.placeholder = this.$t('metadataRule.Number 0-99');
                component.props.max = 99;
                component.props.value = value;

                return component;
            }

            if (MOBILE_VERSION_RULES_LIST.includes(this.rule.id)) {
                return {
                    component: 'metadata-value-select',
                    props: {
                        field: 'value',
                        rule: this.rule,
                        value: this.rule.value,
                    },
                };
            }

            const schema = this.rule.schema === 'list' ? this.rule.elementType : this.rule.schema;
            switch (schema) {
                case 'integer':
                case 'float':
                case 'number':
                case 'aggregation':
                    return getNumberConfig(this.rule, 'value', -Infinity);
                case 'time':
                case 'date':
                    if (['withinLast', '!withinLast', 'withinNext', '!withinNext'].includes(this.rule.operator)) {
                        return getRelativeDateRangeConfig(this.rule);
                    }

                    if (['between'].includes(this.rule.operator)) {
                        return getAbsoluteDateRangeConfig(this.rule);
                    }

                    if (['>=', '<='].includes(this.rule.operator)) {
                        return getSingleDateConfig(this.rule, 'value');
                    }

                    return;
                case 'name':
                case 'os':
                case 'deviceType':
                    return {
                        component: 'simple-select',
                        props: {
                            searchable: true,
                            allowCustomValue: true,
                            field: 'value',
                            options: deviceValuesByField({ platform: ['web', 'mobile'] })[schema],
                            value: this.rule.value,
                        },
                    };
                default:
                    if (['!listContains', 'listContains'].includes(this.rule.operator)) {
                        return getCsvConfig(this.rule);
                    }

                    return getStringConfig(this.rule, 'value');
            }
        },
    },
    methods: {
        processInput ($event) {
            if (this.rule.schema === 'float') {
                let floatValue = parseFloat($event.value);
                floatValue = isNaN(floatValue) ? $event.value : floatValue;

                const parsedEvent = {
                    ...$event,
                    value: floatValue,
                };

                return this.$emit('input', parsedEvent);
            }
            if (this.rule.schema === 'deviceModel') {
                const lowerCaseValue = $event.value.toLowerCase();
                const parsedEvent = {
                    ...$event,
                    value: lowerCaseValue,
                };

                return this.$emit('input', parsedEvent);
            }

            return this.$emit('input', $event);
        },
    },
};
</script>

<style scoped lang="less">
.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
